import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './LoginPage.css';

export default function LoginPage() {
  const { login, register, loginWithGoogle } = useAuth();
  const [mode, setMode] = useState('login'); // 'login' | 'register'
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [dataConsent, setDataConsent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
    setError('');
  };

  const AUTH_ERROR_MESSAGES = {
    AUTH_USER_NOT_FOUND: 'No account found with that email address.',
    AUTH_INVALID_PASSWORD: 'Incorrect password. Please try again.',
    AUTH_DB_ERROR: 'Service temporarily unavailable, please try again shortly.',
    AUTH_SERVER_ERROR: 'Something went wrong on our end. Please try again.',
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      if (mode === 'login') {
        await login(form.email, form.password);
      } else {
        await register(form.name, form.email, form.password, dataConsent);
      }
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('[LoginPage] Auth error:', err);
      }
      const code = err.response?.data?.error;
      setError(AUTH_ERROR_MESSAGES[code] || err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-header">
          <span className="login-logo">🍷</span>
          <h1>Winesup</h1>
          <p>Your AI wine sommelier</p>
        </div>

        <div className="login-tabs">
          <button
            className={mode === 'login' ? 'active' : ''}
            onClick={() => { setMode('login'); setError(''); }}
          >
            Sign In
          </button>
          <button
            className={mode === 'register' ? 'active' : ''}
            onClick={() => { setMode('register'); setError(''); }}
          >
            Create Account
          </button>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {mode === 'register' && (
            <input
              type="text"
              name="name"
              placeholder="Your name (optional)"
              value={form.name}
              onChange={handleChange}
              autoComplete="name"
            />
          )}
          <input
            type="email"
            name="email"
            placeholder="Email address"
            value={form.email}
            onChange={handleChange}
            required
            autoComplete="email"
          />
          <input
            type="password"
            name="password"
            placeholder={mode === 'register' ? 'Password (8+ characters)' : 'Password'}
            value={form.password}
            onChange={handleChange}
            required
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
          />
          {mode === 'register' && (
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '0.8rem', color: '#555', margin: '4px 0' }}>
              <input
                type="checkbox"
                checked={dataConsent}
                onChange={(e) => setDataConsent(e.target.checked)}
                style={{ marginTop: '2px', flexShrink: 0 }}
              />
              I consent to Winesup processing my personal data to provide the service, as described in our Privacy Policy.
            </label>
          )}
          {error && <p className="login-error">{error}</p>}
          <button type="submit" className="btn-primary" disabled={loading || (mode === 'register' && !dataConsent)}>
            {loading ? 'Please wait...' : mode === 'login' ? 'Sign In' : 'Create Account'}
          </button>
        </form>

        <div className="login-divider"><span>or</span></div>

        <div className="oauth-buttons">
          <button className="btn-google" onClick={loginWithGoogle} type="button">
            <svg viewBox="0 0 24 24" width="18" height="18">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>

        </div>

        <p className="login-footer">
          {mode === 'login' ? (
            <>No account? <button onClick={() => setMode('register')}>Create one free</button></>
          ) : (
            <>Already have an account? <button onClick={() => setMode('login')}>Sign in</button></>
          )}
        </p>

        <div className="pricing-hint">
          <p>Free tier includes basic wine pairing</p>
          <p><strong>Premium ($1.99/mo)</strong> — full AI sommelier, menu scanning</p>
        </div>
      </div>
    </div>
  );
}
